/**
 * EHR Tasks report: the buckets, each bucket's columns, and the cell formatting, shared by
 * the page's tables and its CSV / PDF exports (services/csvExport.js), so the three agree.
 *
 * Bucket keys and order match EHR_TASK_BUCKETS in the report Lambdas and the Task Types
 * filter (utils/ehrTaskTypes.js). Records arrive already in bucket order, oldest received
 * first (docs/EHR_TASKS_REPORT_PLAN.md section 4.1).
 *
 * pageOnly columns (care level, staff phone) show on the signed-in page and never leave the
 * portal: no CSV, no PDF (plan decision 6b, minimum necessary).
 */

import { DateTime } from 'luxon';
import { convertIsoUTCToLocal } from '../../../libs.jsx';

const NAME = { key: 'name', label: 'Name', format: 'text' };
const RECEIVED = { key: 'received_utc', label: 'Received', format: 'utc' };
const WAITING = { key: 'waiting_days', label: 'Waiting', format: 'days' };

export const EHR_REPORT_BUCKETS = [
    {
        key: 'move_in', section: 'residents', title: 'Move-ins', noun: 'move-ins',
        columns: [
            NAME,
            { key: 'nickname', label: 'Nickname', format: 'text' },
            { key: 'area_name', label: 'Area', format: 'text' },
            { key: 'care_level', label: 'Care Level', format: 'text', pageOnly: true },
            { key: 'move_in_date', label: 'Move-in Date', format: 'date' },
            RECEIVED, WAITING,
        ],
    },
    {
        key: 'relocate', section: 'residents', title: 'Relocations', noun: 'relocations',
        columns: [
            NAME,
            { key: 'old_area_name', label: 'Prev Area', format: 'text' },
            { key: 'area_name', label: 'New Area', format: 'text' },
            RECEIVED, WAITING,
        ],
    },
    {
        key: 'move_out', section: 'residents', title: 'Move-outs', noun: 'move-outs',
        columns: [
            NAME,
            { key: 'area_name', label: 'Area', format: 'text' },
            { key: 'move_out_date', label: 'Move-out Date', format: 'date' },
            RECEIVED, WAITING,
        ],
    },
    {
        key: 'user_add', section: 'users', title: 'Add User', noun: 'add user tasks',
        columns: [
            { ...NAME, label: 'Display Name' },
            { key: 'title', label: 'Title', format: 'text' },
            { key: 'email', label: 'Email', format: 'text' },
            { key: 'phone', label: 'Phone', format: 'text', pageOnly: true },
            RECEIVED, WAITING,
        ],
    },
    {
        key: 'user_remove', section: 'users', title: 'Remove User', noun: 'remove user tasks',
        columns: [
            { ...NAME, label: 'Display Name' },
            { key: 'username', label: 'Username', format: 'text' },
            { key: 'email', label: 'Email', format: 'text' },
            RECEIVED, WAITING,
        ],
    },
];

export const EHR_SECTIONS = [
    { key: 'residents', title: 'Residents' },
    { key: 'users', title: 'Users' },
];

const BY_KEY = new Map(EHR_REPORT_BUCKETS.map((b) => [b.key, b]));

export function ehrBucket(key) {
    return BY_KEY.get(key) || null;
}

/** 'YYYY-MM-DD' → 'MM/DD/YYYY'. String only: the move dates are calendar dates the EHR
    sent, never shifted by an offset (plan 9.5), and a Date() would apply the viewer's zone. */
export function formatEhrDate(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/** Whole days waiting → "Today", "1 day", "5 days". */
export function formatWaitingDays(days) {
    const n = Number(days);
    if (!Number.isFinite(n) || n <= 0) return 'Today';
    return n === 1 ? '1 day' : `${n} days`;
}

/** One cell's display text; '' when there is nothing to show. */
export function formatEhrCell(record, col, tzOffset) {
    const value = record?.[col.key];
    switch (col.format) {
        case 'date':
            return formatEhrDate(value);
        case 'utc':
            return value ? convertIsoUTCToLocal(value, tzOffset) || '' : '';
        case 'days':
            return formatWaitingDays(value);
        default:
            return value == null ? '' : String(value);
    }
}

/** The report's "as of" time (UTC 'YYYY-MM-DD HH:MM:SS') on the community's clock:
    "Fri, Oct 23, 2026 7:00 AM". Shifted in a UTC zone so the viewer's DST never applies. */
export function formatAsOf(asOfUtc, tzOffset) {
    if (!asOfUtc) return '';
    const dt = DateTime.fromSQL(String(asOfUtc), { zone: 'utc' });
    if (!dt.isValid) return '';
    const offset = Number(tzOffset);
    return dt.plus({ hours: Number.isFinite(offset) ? offset : 0 }).toFormat('ccc, LLL d, yyyy h:mm a');
}

/** Columns that may leave the portal (CSV, PDF): everything but the pageOnly ones. */
export function exportColumns(bucket) {
    return bucket.columns.filter((c) => !c.pageOnly);
}
